import React, { useEffect, useRef, useState } from 'react';
import { CalendarCheck, Check, Copy, ExternalLink, Image, Loader2, Save, Upload } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../hooks/useLanguage';
import { bookingLinkApi } from '../services/api';
import AppointmentSetup from '../components/booking/AppointmentSetup';
import './BookingLinkPage.css';

const defaults = {
  title: '',
  subtitle: '',
  slug: '',
  is_published: false,
  logo_url: '',
  cover_url: '',
  primary_color: '#6d5dfc',
  accent_color: '#23c483',
  theme: 'light',
  language: 'he',
  timezone: 'Asia/Jerusalem',
  slot_interval_minutes: 30,
  minimum_notice_hours: 2,
  booking_window_days: 60,
  require_worker_selection: false,
  show_prices: true,
  cancellation_hours: 12,
  confirmation_message: '',
  booking_url: '',
};

export default function BookingLinkPage() {
  const { isRTL } = useLanguage();
  const { t, i18n } = useTranslation();
  const [settings, setSettings] = useState(defaults);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState('');
  const logoRef = useRef(null);
  const coverRef = useRef(null);

  useEffect(() => {
    bookingLinkApi.get()
      .then((res) => res.success && setSettings({ ...defaults, ...res.data }))
      .catch((error) => toast.error(error.message))
      .finally(() => setLoading(false));
  }, []);

  const change = (field, value) => setSettings((current) => ({ ...current, [field]: value }));
  const save = async (nextSettings = settings, successMessage = t('bookingLink.saved')) => {
    setSaving(true);
    try {
      const res = await bookingLinkApi.update(nextSettings);
      setSettings({ ...defaults, ...res.data });
      toast.success(successMessage);
      return true;
    } catch (error) {
      toast.error(error.message);
      return false;
    } finally {
      setSaving(false);
    }
  };
  const togglePublished = (isPublished) => {
    save(
      { ...settings, is_published: isPublished },
      t(isPublished ? 'bookingLink.publishedSuccess' : 'bookingLink.unpublishedSuccess'),
    );
  };
  const upload = async (field, file) => {
    if (!file) return;
    setUploading(field);
    try {
      const res = await bookingLinkApi.upload(field, file);
      change(field, res.data.url);
      toast.success(t('bookingLink.imageUploaded'));
    } catch (error) {
      toast.error(error.message);
    } finally {
      setUploading('');
    }
  };
  const copy = async () => {
    await navigator.clipboard.writeText(settings.booking_url);
    toast.success(t('bookingLink.copied'));
  };

  if (loading) return <div className="booking-link-loading"><Loader2 className="spinner" />{t('bookingLink.loading')}</div>;

  const previewT = i18n.getFixedT(settings.language || i18n.language);

  return (
    <div className={`booking-link-page ${isRTL ? 'rtl' : ''}`}>
      <header className="booking-link-head">
        <div><span className="booking-kicker">{t('bookingLink.kicker')}</span><h1>{t('bookingLink.title')}</h1><p>{t('bookingLink.subtitle')}</p></div>
        <div className="booking-head-actions">
          <label className={`publish-toggle ${saving ? 'disabled' : ''}`}><input type="checkbox" checked={settings.is_published} disabled={saving} onChange={(e) => togglePublished(e.target.checked)} /><span /><b>{settings.is_published ? t('bookingLink.published') : t('bookingLink.draft')}</b></label>
          <button className="btn btn-primary" onClick={() => save()} disabled={saving}>{saving ? <Loader2 className="spinner" size={17} /> : <Save size={17} />}{t('common.save')}</button>
        </div>
      </header>

      <div className="booking-link-share">
        <div className="booking-share-icon"><CalendarCheck size={22} /></div>
        <div><span>{t('bookingLink.publicUrl')}</span><strong>{settings.booking_url}</strong></div>
        <button className="btn btn-secondary" onClick={copy}><Copy size={16} />{t('bookingLink.copy')}</button>
        {settings.is_published && <a className="btn btn-secondary" href={settings.booking_url} target="_blank" rel="noreferrer"><ExternalLink size={16} />{t('bookingLink.open')}</a>}
      </div>

      <div className="booking-builder">
        <div className="booking-editor">
          <section className="booking-config-card">
            <div className="booking-card-title"><div><h2>{t('bookingLink.brandTitle')}</h2><p>{t('bookingLink.brandSubtitle')}</p></div><Image size={20} /></div>
            <div className="booking-image-row">
              <button className="booking-upload-box logo" onClick={() => logoRef.current?.click()}>{settings.logo_url ? <img src={settings.logo_url} alt="" /> : <Upload size={20} />}<span>{uploading === 'logo_url' ? t('bookingLink.uploading') : t('bookingLink.logo')}</span></button>
              <button className="booking-upload-box cover" onClick={() => coverRef.current?.click()}>{settings.cover_url ? <img src={settings.cover_url} alt="" /> : <Upload size={20} />}<span>{uploading === 'cover_url' ? t('bookingLink.uploading') : t('bookingLink.cover')}</span></button>
              <input ref={logoRef} hidden type="file" accept="image/png,image/jpeg,image/webp" onChange={(e) => upload('logo_url', e.target.files?.[0])} />
              <input ref={coverRef} hidden type="file" accept="image/png,image/jpeg,image/webp" onChange={(e) => upload('cover_url', e.target.files?.[0])} />
            </div>
            <div className="booking-form-grid">
              <label>{t('bookingLink.pageTitle')}<input className="input" value={settings.title} onChange={(e) => change('title', e.target.value)} /></label>
              <label>{t('bookingLink.urlName')}<input className="input" value={settings.slug} onChange={(e) => change('slug', e.target.value)} /></label>
              <label className="wide">{t('bookingLink.pageSubtitle')}<textarea className="input" rows="3" value={settings.subtitle} onChange={(e) => change('subtitle', e.target.value)} /></label>
              <label>{t('bookingLink.primaryColor')}<input className="booking-color" type="color" value={settings.primary_color} onChange={(e) => change('primary_color', e.target.value)} /></label>
              <label>{t('bookingLink.accentColor')}<input className="booking-color" type="color" value={settings.accent_color} onChange={(e) => change('accent_color', e.target.value)} /></label>
              <label>{t('bookingLink.theme')}<select className="input" value={settings.theme} onChange={(e) => change('theme', e.target.value)}><option value="light">{t('bookingLink.light')}</option><option value="dark">{t('bookingLink.dark')}</option><option value="auto">{t('bookingLink.device')}</option></select></label>
              <label>{t('bookingLink.pageLanguage')}<select className="input" value={settings.language} onChange={(e) => change('language', e.target.value)}><option value="he">{t('languages.he')}</option><option value="ar">{t('languages.ar')}</option><option value="en">{t('languages.en')}</option></select></label>
            </div>
          </section>

          <section className="booking-config-card">
            <div className="booking-card-title"><div><h2>{t('bookingLink.rulesTitle')}</h2><p>{t('bookingLink.rulesSubtitle')}</p></div></div>
            <div className="booking-form-grid">
              <label>{t('bookingLink.minimumNotice')}<input className="input" type="number" min="0" value={settings.minimum_notice_hours} onChange={(e) => change('minimum_notice_hours', Number(e.target.value))} /></label>
              <label>{t('bookingLink.bookingWindow')}<input className="input" type="number" min="1" value={settings.booking_window_days} onChange={(e) => change('booking_window_days', Number(e.target.value))} /></label>
              <label>{t('bookingLink.slotInterval')}<select className="input" value={settings.slot_interval_minutes} onChange={(e) => change('slot_interval_minutes', Number(e.target.value))}>{[15, 30, 45, 60].map((minutes) => <option key={minutes} value={minutes}>{t('bookingLink.minuteCount', { count: minutes })}</option>)}</select></label>
              <label>{t('bookingLink.cancellationCutoff')}<input className="input" type="number" min="0" value={settings.cancellation_hours} onChange={(e) => change('cancellation_hours', Number(e.target.value))} /></label>
              <label className="booking-check"><input type="checkbox" checked={settings.require_worker_selection} onChange={(e) => change('require_worker_selection', e.target.checked)} /><span><b>{t('bookingLink.requireWorker')}</b><small>{t('bookingLink.requireWorkerHint')}</small></span></label>
              <label className="booking-check"><input type="checkbox" checked={settings.show_prices} onChange={(e) => change('show_prices', e.target.checked)} /><span><b>{t('bookingLink.showPrices')}</b><small>{t('bookingLink.showPricesHint')}</small></span></label>
              <label className="wide">{t('bookingLink.confirmationMessage')}<textarea className="input" rows="3" value={settings.confirmation_message} onChange={(e) => change('confirmation_message', e.target.value)} placeholder={t('bookingLink.confirmationPlaceholder')} /></label>
            </div>
          </section>

          <AppointmentSetup />
        </div>

        <aside className="booking-preview-wrap">
          <div className="booking-preview-label"><span>{t('bookingLink.livePreview')}</span><i /><span>{t('bookingLink.mobile')}</span></div>
          <div className={`booking-phone ${settings.theme === 'dark' ? 'dark' : ''}`} style={{ '--booking-primary': settings.primary_color, '--booking-accent': settings.accent_color }}>
            <div className="booking-phone-cover">{settings.cover_url && <img src={settings.cover_url} alt="" />}</div>
            <div className="booking-phone-body">
              <div className="booking-phone-logo">{settings.logo_url ? <img src={settings.logo_url} alt="" /> : <CalendarCheck size={25} />}</div>
              <h3>{settings.title || previewT('bookingLink.previewBusiness')}</h3><p>{settings.subtitle || previewT('bookingLink.previewSubtitle')}</p>
              <div className="preview-step"><span><Check size={13} /></span><div><b>{previewT('bookingLink.chooseService')}</b><small>{previewT('bookingLink.servicesHint')}</small></div></div>
              <div className="preview-step"><span>2</span><div><b>{previewT('bookingLink.selectTime')}</b><small>{previewT('bookingLink.availabilityHint')}</small></div></div>
              <button>{previewT('bookingLink.bookAppointment')}</button>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
